import { Cartesian3 } from "cesium";
import { edgeKey, computeFaceNormal, } from "../../utilities/mesh-face-selection";
function vertexToCartesian(geometry, vertexIndex) {
    return Cartesian3.fromArray(geometry.vertices, vertexIndex * 3);
}
/**
 * Extract feature edges from geometry.
 * A feature edge is either:
 * - A boundary edge (belongs to exactly one triangle)
 * - A crease edge (dihedral angle between adjacent face normals exceeds threshold)
 */
export function extractFeatureEdges(geometry, angleThresholdDeg = 30) {
    const faceCount = geometry.indices.length / 3;
    const edgeToFaces = new Map();
    const edgeVertices = new Map();
    for (let f = 0; f < faceCount; f++) {
        const idx = f * 3;
        const i0 = geometry.indices[idx];
        const i1 = geometry.indices[idx + 1];
        const i2 = geometry.indices[idx + 2];
        const pairs = [
            [i0, i1],
            [i1, i2],
            [i2, i0],
        ];
        for (const [a, b] of pairs) {
            const key = edgeKey(a, b);
            const faces = edgeToFaces.get(key);
            if (faces) {
                faces.push(f);
            }
            else {
                edgeToFaces.set(key, [f]);
                edgeVertices.set(key, [a, b]);
            }
        }
    }
    const cosThreshold = Math.cos((angleThresholdDeg * Math.PI) / 180);
    const edges = [];
    for (const [key, faces] of edgeToFaces) {
        const verts = edgeVertices.get(key);
        if (!verts)
            continue;
        if (faces.length === 1) {
            edges.push({ a: verts[0], b: verts[1] });
        }
        else if (faces.length === 2) {
            const n0 = computeFaceNormal(geometry, faces[0]);
            const n1 = computeFaceNormal(geometry, faces[1]);
            if (!n0 || !n1)
                continue;
            if (Cartesian3.dot(n0, n1) <= cosThreshold) {
                edges.push({ a: verts[0], b: verts[1] });
            }
        }
        else {
            edges.push({ a: verts[0], b: verts[1] });
        }
    }
    return edges;
}
/**
 * Chain connected edge segments into longer strips for cheaper polyline rendering.
 */
export function chainEdgeSegments(geometry, edges) {
    if (edges.length === 0)
        return [];
    const adjacency = new Map();
    for (let i = 0; i < edges.length; i++) {
        const edge = edges[i];
        const fromA = adjacency.get(edge.a);
        if (fromA) {
            fromA.push(i);
        }
        else {
            adjacency.set(edge.a, [i]);
        }
        const fromB = adjacency.get(edge.b);
        if (fromB) {
            fromB.push(i);
        }
        else {
            adjacency.set(edge.b, [i]);
        }
    }
    const visitedEdges = new Set();
    const strips = [];
    function walkChain(startVertex, firstEdgeIndex) {
        const chain = [startVertex];
        let currentVertex = startVertex;
        let currentEdgeIndex = firstEdgeIndex;
        while (true) {
            visitedEdges.add(currentEdgeIndex);
            const currentEdge = edges[currentEdgeIndex];
            const nextVertex = currentEdge.a === currentVertex ? currentEdge.b : currentEdge.a;
            chain.push(nextVertex);
            currentVertex = nextVertex;
            const nextEdgeIndex = (adjacency.get(currentVertex) ?? []).find((edgeIndex) => !visitedEdges.has(edgeIndex));
            if (nextEdgeIndex === undefined) {
                break;
            }
            currentEdgeIndex = nextEdgeIndex;
        }
        return chain;
    }
    for (const [vertex, incidentEdges] of adjacency) {
        if (incidentEdges.length === 2)
            continue;
        for (const edgeIndex of incidentEdges) {
            if (visitedEdges.has(edgeIndex))
                continue;
            const chain = walkChain(vertex, edgeIndex);
            if (chain.length >= 2) {
                strips.push(chain.map((index) => vertexToCartesian(geometry, index)));
            }
        }
    }
    for (let edgeIndex = 0; edgeIndex < edges.length; edgeIndex++) {
        if (visitedEdges.has(edgeIndex))
            continue;
        const startVertex = edges[edgeIndex].a;
        const chain = walkChain(startVertex, edgeIndex);
        if (chain.length >= 2) {
            strips.push(chain.map((index) => vertexToCartesian(geometry, index)));
        }
    }
    return strips;
}
//# sourceMappingURL=feature-edge.js.map